import IcEdit from '@assets/details-camera-icons/IcEdit';
import { pushError, pushSuccess } from '@components/toast';
import { appConfig } from '@configs/index';
import {
    cameraPhotoVehicleHttpController,
    potentialGroupContraventionController,
    potentialPhotoContraventionController,
} from '@controllers/index';
import { IUpdatePotentialContraventionPhotoVehicle } from '@EcamModel/controllers/IPotentialContraventionPhotoHttpController';
import { CameraPhotoVehicles, GroupPotentialContraventions, PhotoType, PhotoVersion } from '@EcamModel/model';
import { VerificationAudit } from '@EcamModel/model/VerificationAudit';
import { Box, Button, CircularProgress, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { PREVIEW_RATIO } from '@pages/cameras/hook/CameraDetailsProvider';
import NoDataCameras from '@pages/cameras/list/components/NoDataCameras';
import { useImageDimensions, invalidateImageCache } from '@pages/cameras/live-photos/components/PreviewImage';
import { getCenterFrame } from '@pages/cameras/details/utils';
import color from '@theme/Colors';
import { orderBy } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { MdClose, MdDone, MdOutlineTimeline, MdZoomIn } from 'react-icons/md';
import { Image as KonvaImage, Layer, Rect, Stage } from 'react-konva';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { adjustPreviewSize } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import useImage from 'use-image';
import { PCInfo } from '..';
import { usePotentialContravention } from '../hooks/PotentialContraventionProvider';
import { CropArea } from '../hooks/useEditImage';
import { PhotoItem } from './EvidencePhotoGroup';
import {
    areCropAreasEqual,
    convertToCropArea,
    displayHeightCropPlate,
    DisplayStage,
    displayWidthCropPlate,
} from './PopUpPlateEditing';
import TimeOnPicture from './TimeOnPicture';
import { DrawerContext } from '@components/drawer/useDrawer';
import { useImagePreviewDimensions } from '../hooks/useImageDimensions';
import { useVerificationQueueMode } from '@pages/verification/hooks/VerificationProvider';
import { photoTypeColorMap } from '@pages/manual-create-pcn/configs';

export type PhotoProps = {
    title?: string;
    date: Date;
    blob?: string;
    rootBlob?: string;
    sxBox?: SxProps<Theme>;
    sxBottom?: SxProps<Theme>;
    plate?: string;
    timeZone?: string;
    photoType: PhotoType;
    VerificationAudit?: VerificationAudit;
    handleClick?(url: string): void;
    handleEditImg?(): void;
    handlePlateEdit?(): void;
    handlePlateScaleEdit?(): void;
    handleReplacePhoto?(): void;
    onJumpToTimeline?(): void;
    photo: CameraPhotoVehicles;
    readonly?: boolean;
    plateRootImage?: string;
    selectedItem?: PhotoItem;
    photos: Partial<PhotoItem>[];
    prevGeoZones?: { x: number; y: number; geoZoneId: number | undefined }[][];
    vehicleBoundingBoxes?: { x: number; y: number; width: number; height: number; plate?: string }[];
    geoZoneDisplayWidth?: number;
};

export default function EvidencePhotoItem({
    title,
    date,
    sxBottom,
    blob,
    rootBlob,
    photoType,
    plate: vehiclePlate,
    sxBox,
    timeZone,
    photos,
    ...props
}: PhotoProps) {
    const potentialContraventionContext = usePotentialContravention();
    const { setSelectedPc, setGroupPc, contraventionId } = potentialContraventionContext;
    const { verificationQueueMode } = useVerificationQueueMode();
    const queryClient = useQueryClient();
    const backdrop = useBackdrop();
    const navigate = useNavigate();
    const { open: openDrawer } = useContext(DrawerContext);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isPlateHovered, setIsPlateHovered] = useState(false);
    const [isCtrlHeld, setIsCtrlHeld] = useState(false);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Control' && !e.repeat) setIsCtrlHeld((prev) => !prev);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    const [originalImage, setOriginalImage] = useState<(HTMLImageElement | undefined)[]>([]);
    const [croppedImage, setCroppedImage] = useState<HTMLImageElement | null>(null);
    const [imageBase64, setImageBase64] = useState<string | null>(null);

    const [isCropDisabled, setIsCropDisabled] = useState<Record<PhotoType, boolean>>({
        [PhotoType.Entry]: true,
        [PhotoType.Validation]: true,
        [PhotoType.Overview]: true,
        [PhotoType.Exit]: true,
        [PhotoType.UnobstructedPlate]: true,
    });

    const [containerSize, setContainerSize] = useState({
        width: (window.innerWidth * 0.2) / 2,
        height: ((window.innerWidth * 0.2) / 2) * PREVIEW_RATIO,
        minHeight: 0,
    });

    useEffect(() => {
        const updateStageSize = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                const height = width * PREVIEW_RATIO;
                setContainerSize({ width, height, minHeight: height + 10 });
            } else {
                setTimeout(updateStageSize, 300);
            }
        };

        updateStageSize();
        window.addEventListener('resize', updateStageSize);

        return () => {
            window.removeEventListener('resize', updateStageSize);
        };
    }, []);

    const { image, loading, isError } = useImageDimensions(blob);
    const { image: rootImage, loading: rootLoading, isError: rootError } = useImageDimensions(rootBlob);

    const imageUrl = appConfig.gateway.blobURL + blob;

    const [imageRootBlob] = useImage(imageUrl, 'anonymous');
    const currentImage = originalImage[originalImage.length - 1] || imageRootBlob;

    const calculateRectangleDimensions = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number
    ) => {
        const width = Math.abs(x2 - x1);
        const height = Math.abs(y3 - y1);
        return { width, height };
    };

    const { X1, X2, X3, X4, Y1, Y2, Y3, Y4 } = props?.photo ?? ({} as CameraPhotoVehicles);
    const plate = calculateRectangleDimensions(X1, Y1, X2, Y2, X3, Y3, X4, Y4);

    const { previewWidth, previewHeight } = adjustPreviewSize(
        plate.width,
        plate.height,
        containerSize.width,
        containerSize.height
    );

    const rootImageWidth = rootImage?.width || 1920;
    const scaleSize = rootImageWidth / displayWidthCropPlate;

    const initialCropArea =
        props?.photo && image
            ? convertToCropArea({
                  X1: (X1 / image?.width) * displayWidthCropPlate,
                  X2: (X2 / image?.width) * displayWidthCropPlate,
                  X3: (X3 / image?.width) * displayWidthCropPlate,
                  X4: (X4 / image?.width) * displayWidthCropPlate,
                  Y1: (Y1 / image?.height) * displayHeightCropPlate,
                  Y2: (Y2 / image?.height) * displayHeightCropPlate,
                  Y3: (Y3 / image?.height) * displayHeightCropPlate,
                  Y4: (Y4 / image?.height) * displayHeightCropPlate,
              })
            : null;

    const [cropArea, setCropArea] = useState<CropArea | null>(initialCropArea);

    const cropAnchors = [
        {
            x: cropArea?.x!,
            y: cropArea?.y!,
        },
        {
            x: cropArea?.x! + cropArea?.width!,
            y: cropArea?.y!,
        },
        {
            x: cropArea?.x!,
            y: cropArea?.y! + cropArea?.height!,
        },
        {
            x: cropArea?.x! + cropArea?.width!,
            y: cropArea?.y! + cropArea?.height!,
        },
    ];

    const resultCrop = {
        X1: cropAnchors[0].x,
        X2: cropAnchors[1].x,
        X3: cropAnchors[2].x,
        X4: cropAnchors[3].x,
        Y1: cropAnchors[0].y,
        Y2: cropAnchors[1].y,
        Y3: cropAnchors[2].y,
        Y4: cropAnchors[3].y,
    };

    const handleScaleChange = (areaScaleFactor: number) => {
        setCropArea((prev) => {
            const originalCropArea = prev || initialCropArea;
            if (!originalCropArea || !initialCropArea) return prev;

            const centerX = originalCropArea.x + originalCropArea.width / 2;
            const centerY = originalCropArea.y + originalCropArea.height / 2;

            const currentWidth = originalCropArea.width;
            const currentHeight = originalCropArea.height;

            const limitMinWidthCropPlate = 5;
            const limitMinHeightCropPlate = 5;

            if (
                areaScaleFactor > 1 &&
                (currentWidth <= limitMinWidthCropPlate || currentHeight <= limitMinHeightCropPlate)
            ) {
                return prev;
            }

            // area = width * height
            const originalArea = currentWidth * currentHeight;
            // aspectRatio = width / height
            const aspectRatio = currentWidth / currentHeight;

            const newArea = originalArea / areaScaleFactor;

            // area = width * height
            // aspectRatio = width / height
            // => height = width / aspectRatio
            // => area = width * (width / aspectRatio) = width^2 / aspectRatio
            // => width^2 = area * aspectRatio
            // => width = sqrt(area * aspectRatio)
            let newWidth = Math.sqrt(newArea * aspectRatio);
            let newHeight = newWidth / aspectRatio;

            // Frame crop limit
            newWidth = Math.min(newWidth, displayWidthCropPlate);
            newHeight = Math.min(newHeight, displayHeightCropPlate);

            let newX = centerX - newWidth / 2;
            let newY = centerY - newHeight / 2;

            // Ensure it does not exceed the screen
            if (newX + newWidth > displayWidthCropPlate) newX = displayWidthCropPlate - newWidth;
            if (newY + newHeight > displayHeightCropPlate) newY = displayHeightCropPlate - newHeight;

            return { x: newX, y: newY, width: newWidth, height: newHeight };
        });
    };

    const handleCropImage = () => {
        if (!currentImage || !cropArea) return;

        const imageWidth = currentImage.width;
        const imageHeight = currentImage.height;

        const cropX = (cropArea.x / displayWidthCropPlate) * imageWidth;
        const cropY = (cropArea.y / displayHeightCropPlate) * imageHeight;
        const cropWidth = (cropArea.width / displayWidthCropPlate) * imageWidth;
        const cropHeight = (cropArea.height / displayHeightCropPlate) * imageHeight;

        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = cropWidth;
        croppedCanvas.height = cropHeight;

        const ctx = croppedCanvas.getContext('2d')!;
        ctx.drawImage(currentImage, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

        const croppedImage = new window.Image();
        croppedImage.src = croppedCanvas.toDataURL();

        setCroppedImage(croppedImage);

        croppedImage.onload = () => {
            if (!originalImage) {
                setOriginalImage((prev) => [...prev, croppedImage]);
            }
            setIsCropDisabled((prev) => ({ ...prev, [photoType]: false }));

            const dataURL = croppedCanvas.toDataURL('image/jpeg');
            setImageBase64(dataURL);
        };
    };

    const handleUpdatePhotoVehicle = async () => {
        const selectedItem = props.selectedItem;
        if (!selectedItem) return;

        const _filter: IUpdatePotentialContraventionPhotoVehicle = {
            brightness: 0,
            contrast: 0,
            imageBase64: imageBase64 ?? '',
            photoVehicleId: selectedItem?.photo?.Id! ?? undefined,
            photoType: selectedItem.photoType,
            GroupPotentialContraventionId: contraventionId,
        };
        await potentialPhotoContraventionController
            .updatePhotoVehicle(_filter)
            .then(async (res) => {
                setSelectedPc((prevSelectedPc) => {
                    if (!prevSelectedPc) return prevSelectedPc;
                    if (prevSelectedPc.Id === res.PotentialContraventionId) {
                        return {
                            ...prevSelectedPc,
                            PotentialContraventionPhotos: prevSelectedPc.PotentialContraventionPhotos?.map((photo) =>
                                photo.CameraPhotoVehicleId === res.CameraPhotoVehicleId ? res : photo
                            ),
                        };
                    }
                    return prevSelectedPc;
                });
                queryClient.invalidateQueries(['trailAuditEventLogPCN']);
            })
            .catch((error) => {
                pushError(error?.response?.data?.message);
            })
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const handleUpsertPhotoVehicle = async () => {
        const selectedItem = props.selectedItem?.photo;
        if (!selectedItem) return;

        backdrop.setTrue();
        const { X1, X2, X3, X4, Y1, Y2, Y3, Y4 } = resultCrop;

        const photoVehicle: CameraPhotoVehicles = {
            CameraPhotoId: selectedItem?.CameraPhotoId ?? 0,
            Plate: selectedItem?.Plate ?? '',
            Id: selectedItem?.Id,
            X1: X1 * scaleSize,
            X2: X2 * scaleSize,
            X3: X3 * scaleSize,
            X4: X4 * scaleSize,
            Y1: Y1 * scaleSize,
            Y2: Y2 * scaleSize,
            Y3: Y3 * scaleSize,
            Y4: Y4 * scaleSize,
        };

        await cameraPhotoVehicleHttpController
            .updateCameraPhotoVehicleCoordinates(photoVehicle)
            .then(async (_) => {
                await handleUpdatePhotoVehicle();
                backdrop.setTrue();
                await potentialGroupContraventionController
                    .getGroupPotentialContravention(contraventionId)
                    .then(async (res) => {
                        invalidateImageCache();
                        pushSuccess('Saved successfully');
                        const _res: GroupPotentialContraventions = {
                            ...res,
                            PotentialContraventions: orderBy(res.PotentialContraventions, (pc) => -pc.Step),
                        };
                        setGroupPc(_res);
                        setSelectedPc(_res.PotentialContraventions![0] as PCInfo);
                    })
                    .catch((err) => navigate(`/${verificationQueueMode}`))
                    .finally(() => {
                        backdrop.setFalse();
                    });
            })
            .catch((error) => {
                pushError(error?.response?.data?.message);
            })
            .finally(() => {
                backdrop.setFalse();
                handleCancelOrFinally();
            });
    };

    const handleCancelOrFinally = () => {
        setCroppedImage(null);
        setCropArea(null);
        setIsCropDisabled((prev) => ({ ...prev, [photoType]: true }));
    };

    const isHiddenActionCropPlate =
        !isCropDisabled[photoType] &&
        !areCropAreasEqual({
            original: initialCropArea ?? ({} as CropArea),
            edited: cropArea ?? ({} as CropArea),
        });

    useEffect(() => {
        if (cropArea) {
            handleCropImage();
        }
    }, [cropArea]);

    const ptColor = photoTypeColorMap[photoType] ?? photoTypeColorMap[PhotoType.Entry];

    return (
        <Stack
            width={'100%'}
            border={`2px solid ${ptColor.main}`}
            bgcolor={ptColor.bg}
            borderRadius={1}
            display="flex"
            justifyContent="center"
        >
            <Stack sx={{ width: `100%` }} direction={'column'}>
                <Stack direction={'row'} width={'100%'} spacing={1}>
                    <Stack direction={'column'} width={'100%'}>
                        {title ? (
                            <Stack
                                width={'100%'}
                                direction={'row'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                sx={{
                                    background: ptColor.main,
                                    p: '8px 16px',
                                    textAlign: 'center',
                                    color: 'white',
                                }}
                            >
                                <Stack
                                    width={'100%'}
                                    direction={'row'}
                                    spacing={0.5}
                                    justifyContent={'flex-start'}
                                    alignItems="center"
                                >
                                    <Typography
                                        variant="body2"
                                        color="#fff"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.handleClick?.('');
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                    {(!props.readonly || !props.VerificationAudit) && (
                                        <Stack
                                            onClick={() => {
                                                props.handleEditImg?.();
                                            }}
                                            sx={{
                                                '&:hover': {
                                                    color: (theme) => theme.palette.primary.main,
                                                    cursor: 'pointer',
                                                    transition: '.2s ease-in-out',
                                                },
                                            }}
                                        >
                                            <IcEdit />
                                        </Stack>
                                    )}
                                </Stack>
                                {props.onJumpToTimeline && (
                                    <Tooltip title="Jump to timeline" arrow placement="top">
                                        <Stack
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                props.onJumpToTimeline?.();
                                            }}
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.4}
                                            sx={{
                                                cursor: 'pointer',
                                                opacity: 0.85,
                                                transition: 'opacity 0.2s ease-in-out',
                                                '&:hover': {
                                                    opacity: 1,
                                                },
                                            }}
                                        >
                                            <MdOutlineTimeline size={14} color="#fff" />
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Stack>
                        ) : (
                            <Box></Box>
                        )}

                        <Box
                            width={'100%'}
                            sx={{
                                ...sxBox,
                                background: `url(${image?.src})`,
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'cover',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                cursor: props.handleClick ? 'pointer' : '',
                                position: 'relative',
                                aspectRatio: '16/9',
                                overflow: 'hidden',
                            }}
                            onClick={() => image && props.handleClick?.(image?.src)}
                            onMouseEnter={() => setIsPlateHovered(true)}
                            onMouseLeave={() => setIsPlateHovered(false)}
                        >
                            {loading && (
                                <Stack flexBasis={1} justifyContent="center" minHeight={200}>
                                    <CircularProgress size={20} sx={{ margin: 'auto' }} />
                                </Stack>
                            )}

                            {isError && (
                                <Stack flexBasis={1} justifyContent="center" minHeight={200}>
                                    <NoDataCameras />
                                </Stack>
                            )}

                            {/* Bottom gradient for readability */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    height: '15%',
                                    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
                                    pointerEvents: 'none',
                                    zIndex: 0,
                                }}
                            />

                            <Box sx={{ position: 'absolute', bottom: 2, right: 2, zIndex: 2, pointerEvents: 'none' }}>
                                <TimeOnPicture date={date} timezone={timeZone} />
                            </Box>

                            {!loading &&
                                !isError &&
                                image &&
                                (() => {
                                    const vbW = props.geoZoneDisplayWidth ?? image.width;
                                    const vbH = props.geoZoneDisplayWidth
                                        ? props.geoZoneDisplayWidth * (9 / 16)
                                        : image.height;

                                    const displayScaleSize = (rootImage?.width || 1920) / vbW;
                                    const displayCropArea =
                                        props.photo && rootImage
                                            ? convertToCropArea({
                                                  X1: props.photo.X1 / displayScaleSize,
                                                  X2: props.photo.X2 / displayScaleSize,
                                                  X3: props.photo.X3 / displayScaleSize,
                                                  X4: props.photo.X4 / displayScaleSize,
                                                  Y1: props.photo.Y1 / displayScaleSize,
                                                  Y2: props.photo.Y2 / displayScaleSize,
                                                  Y3: props.photo.Y3 / displayScaleSize,
                                                  Y4: props.photo.Y4 / displayScaleSize,
                                              })
                                            : null;

                                    return (
                                        <svg
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                pointerEvents: 'none',
                                                overflow: 'hidden',
                                                transition: 'opacity 0.35s ease-in-out',
                                                opacity: isPlateHovered || isCtrlHeld ? 0 : 1,
                                            }}
                                            viewBox={`0 0 ${vbW} ${vbH}`}
                                            preserveAspectRatio="xMidYMid slice"
                                        >
                                            {props.prevGeoZones
                                                ?.filter((frame) => frame[0]?.geoZoneId)
                                                .map((frame, fi) => {
                                                    if (!frame?.length) return null;
                                                    const pts = frame.map((p) => `${p.x},${p.y}`).join(' ');
                                                    return (
                                                        <g key={`gz-${fi}`}>
                                                            <polyline
                                                                points={pts}
                                                                stroke={color.accentPink}
                                                                strokeWidth={5}
                                                                strokeDasharray="20 10"
                                                                 fill="rgba(0, 0, 0, 0.12)"
                                                                strokeLinejoin="round"
                                                            />
                                                        </g>
                                                    );
                                                })}

                                            {displayCropArea && (
                                                <rect
                                                    x={displayCropArea.x}
                                                    y={displayCropArea.y}
                                                    width={displayCropArea.width}
                                                    height={displayCropArea.height}
                                                    stroke="red"
                                                    strokeWidth={4}
                                                    fill="none"
                                                />
                                            )}

                                            {props.vehicleBoundingBoxes?.map((bbox, i) => (
                                                <g key={`bbox-${i}`}>
                                                    <rect
                                                        x={bbox.x}
                                                        y={bbox.y}
                                                        width={bbox.width}
                                                        height={bbox.height}
                                                        stroke={photoTypeColorMap[photoType]?.main ?? color.mint}
                                                        strokeWidth={6}
                                                        fill="none"
                                                    />
                                                </g>
                                            ))}
                                        </svg>
                                    );
                                })()}

                            {!isError && !rootLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        opacity: isPlateHovered || isCtrlHeld ? 1 : 0,
                                        transform: isPlateHovered || isCtrlHeld ? 'scale(1)' : 'scale(0.97)',
                                        transition: 'opacity 0.35s ease-in-out, transform 0.35s ease-in-out',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        background: 'rgba(0, 0, 0, 0.85)',
                                        zIndex: 2,
                                        pointerEvents: isPlateHovered || isCtrlHeld ? 'auto' : 'none',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        props.handleClick?.('');
                                    }}
                                >
                                    <Box
                                        ref={containerRef}
                                        sx={{
                                            flex: 1,
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {croppedImage !== null ? (
                                            <DisplayStage
                                                image={croppedImage}
                                                width={openDrawer ? containerSize.width + 23 : containerSize.width}
                                                height={containerSize.height}
                                            />
                                        ) : props.plateRootImage ? (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    background: `url(${appConfig.gateway.blobURL}${props.plateRootImage}) no-repeat center center`,
                                                    backgroundSize: 'contain',
                                                }}
                                            />
                                        ) : rootImage ? (
                                            <Stage width={containerSize.width} height={containerSize.height}>
                                                <Layer>
                                                    <Rect
                                                        width={containerSize.width}
                                                        height={containerSize.height}
                                                        fill={ptColor.bg}
                                                        opacity={0.2}
                                                    />
                                                    <KonvaImage
                                                        x={(containerSize.width - previewWidth) / 2}
                                                        y={(containerSize.height - previewHeight) / 2}
                                                        width={previewWidth}
                                                        height={previewHeight}
                                                        image={rootImage}
                                                        crop={{
                                                            x: X1,
                                                            y: Y1,
                                                            width: plate.width,
                                                            height: plate.height,
                                                        }}
                                                    />
                                                </Layer>
                                            </Stage>
                                        ) : null}
                                    </Box>

                                    {/* Bottom toolbar for plate actions */}
                                    <Stack
                                        direction={'row'}
                                        justifyContent={'space-between'}
                                        alignItems={'center'}
                                        sx={{
                                            width: '100%',
                                            p: '6px 12px',
                                            background: 'rgba(0, 0, 0, 0.6)',
                                            backdropFilter: 'blur(4px)',
                                        }}
                                    >
                                        <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                                                Plate zone
                                            </Typography>
                                            {!isError && !props.VerificationAudit && !isHiddenActionCropPlate && (
                                                <Tooltip title="Edit plate" arrow placement="top">
                                                    <Stack
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            props.handlePlateEdit?.();
                                                        }}
                                                        sx={{
                                                            color: '#ccc',
                                                            '&:hover': {
                                                                color: '#fff',
                                                                cursor: 'pointer',
                                                                transition: '0.3s ease-in-out',
                                                            },
                                                        }}
                                                        alignItems="center"
                                                    >
                                                        <IcEdit width={14} height={14} />
                                                    </Stack>
                                                </Tooltip>
                                            )}
                                        </Stack>

                                        <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
                                            {/* Save / Cancel when crop changed */}
                                            {isHiddenActionCropPlate && (
                                                <>
                                                    <Tooltip placement="top" title="Save">
                                                        <Box
                                                            sx={{
                                                                width: 22,
                                                                height: 22,
                                                                background: (theme) => theme.palette.primary.main,
                                                                borderRadius: 2,
                                                                color: '#fff',
                                                                '&:hover': {
                                                                    background: (theme) => theme.palette.primary.dark,
                                                                    cursor: 'pointer',
                                                                    transition: '0.3s ease-in-out',
                                                                },
                                                            }}
                                                            display={'flex'}
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpsertPhotoVehicle();
                                                            }}
                                                        >
                                                            <MdDone style={{ width: 16, height: 16 }} />
                                                        </Box>
                                                    </Tooltip>
                                                    <Tooltip placement="top" title="Close">
                                                        <Box
                                                            sx={{
                                                                width: 22,
                                                                height: 22,
                                                                background: (theme) => theme.palette.error.main,
                                                                borderRadius: 2,
                                                                color: '#fff',
                                                                '&:hover': {
                                                                    background: (theme) => theme.palette.error.dark,
                                                                    cursor: 'pointer',
                                                                    transition: '0.3s ease-in-out',
                                                                },
                                                            }}
                                                            display={'flex'}
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCancelOrFinally();
                                                            }}
                                                        >
                                                            <MdClose style={{ width: 16, height: 16 }} />
                                                        </Box>
                                                    </Tooltip>
                                                </>
                                            )}

                                            {/* Scale buttons */}
                                            {[2, 0.5].map((scaleFactor) => (
                                                <Button
                                                    key={scaleFactor}
                                                    size="small"
                                                    variant="cancelOutlined"
                                                    sx={{
                                                        minWidth: '0px',
                                                        padding: '0px 6px',
                                                        color: '#fff',
                                                        border: `1px solid rgba(255,255,255,0.4)`,
                                                        bgcolor: 'rgba(255,255,255,0.1)',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(255,255,255,0.1)',
                                                            border: `1px solid rgba(255,255,255,0.7)`,
                                                        },
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleScaleChange(scaleFactor);
                                                        props.handlePlateScaleEdit?.();
                                                    }}
                                                >
                                                    {scaleFactor === 2 ? 'x2' : '÷2'}
                                                </Button>
                                            ))}
                                        </Stack>
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
}
